import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CartErrorEvent } from '@theme/events';

/**
 * Product Bundle Component
 * Handles bundle product selection, variant updates, and cart submission
 */
export class ProductBundleComponent extends Component {
  requiredRefs = [
    'bundleSizePicker',
    'bundleAddToCart',
    'bundleQuantity',
    'bundleForm',
    'bundleVariant1Id',
    'bundleVariant2Id',
    'bundleFormQuantity'
  ];

  /** @type {{
    product1: any,
    product2: any,
    discountPercentage: number,
    selectedVariants: {
      product1: any,
      product2: any
    },
    currentSize: string | null
  }} */
  #bundleData = {
    product1: null,
    product2: null,
    discountPercentage: 0,
    selectedVariants: {
      product1: null,
      product2: null
    },
    currentSize: null
  };

  /** @type {boolean} */
  #isLoading = false;

  /** @type {number} */
  #retryCount = 0;

  connectedCallback() {
    super.connectedCallback();
    // Hide main product prices initially
    this.#hideMainProductPrices();
    // Add a small delay to ensure DOM is fully ready
    setTimeout(() => {
      this.#initializeBundle();
      this.#bindEvents();
    }, 100);
  }

  /**
   * Initialize bundle data and load product information
   */
  async #initializeBundle() {
    const product1Id = this.getAttribute('data-product-1-id');
    const product2Id = this.getAttribute('data-product-2-id');
    const product1Handle = this.getAttribute('data-product-1-handle');
    const product2Handle = this.getAttribute('data-product-2-handle');
    const discountPercentage = parseInt(this.getAttribute('data-bundle-discount')) || 35;



    this.#bundleData.discountPercentage = discountPercentage;

    // Validate that we have product IDs or handles
    if (!product1Id && !product1Handle) {
      console.error('Product Bundle: No product 1 ID or handle found');
      // Retry after a short delay in case data is still loading (max 3 retries)
      if (this.#retryCount < 3) {
        this.#retryCount++;
        setTimeout(() => this.#initializeBundle(), 500);
      }
      return;
    }
    if (!product2Id && !product2Handle) {
      console.error('Product Bundle: No product 2 ID or handle found');
      // Retry after a short delay in case data is still loading (max 3 retries)
      if (this.#retryCount < 3) {
        this.#retryCount++;
        setTimeout(() => this.#initializeBundle(), 500);
      }
      return;
    }

    try {
      // Load both products data using handle (preferred) or ID as fallback
      const product1Identifier = product1Handle || product1Id;
      const product2Identifier = product2Handle || product2Id;
      
      if (!product1Identifier || !product2Identifier) {
        console.error('Product Bundle: Missing product identifiers');
        return;
      }



      // Load products individually to handle errors gracefully
      let product1Data, product2Data;
      
      try {
        product1Data = await this.#loadProductData(product1Identifier);
      } catch (error) {
        console.error('Failed to load product 1:', error);
        return;
      }
      
      try {
        product2Data = await this.#loadProductData(product2Identifier);
      } catch (error) {
        console.error('Failed to load product 2:', error);
        return;
      }

      this.#bundleData.product1 = product1Data;
      this.#bundleData.product2 = product2Data;

      // Validate that products have variants
      if (!product1Data.variants || product1Data.variants.length === 0) {
        throw new Error(`Product ${product1Id} has no variants`);
      }
      if (!product2Data.variants || product2Data.variants.length === 0) {
        throw new Error(`Product ${product2Id} has no variants`);
      }

      // Set initial size if available and find matching variants
      if (this.refs.bundleSizePicker) {
        const sizePicker = this.refs.bundleSizePicker;
        if (sizePicker && typeof sizePicker.value === 'string') {
          this.#bundleData.currentSize = sizePicker.value;
          
          // Find variants that match the selected size for both products
          const initialVariant1 = this.#findVariantBySizeAndCurrentSelections(product1Data, this.#bundleData.currentSize, 1);
          const initialVariant2 = this.#findVariantBySizeAndCurrentSelections(product2Data, this.#bundleData.currentSize, 2);
          
          if (initialVariant1) {
            this.#bundleData.selectedVariants.product1 = initialVariant1;
          } else {
            this.#bundleData.selectedVariants.product1 = product1Data.variants[0];
          }
          
          if (initialVariant2) {
            this.#bundleData.selectedVariants.product2 = initialVariant2;
          } else {
            this.#bundleData.selectedVariants.product2 = product2Data.variants[0];
          }
        }
      } else {
        // Fallback to first variants if no size picker
        this.#bundleData.selectedVariants.product1 = product1Data.variants?.[0] ?? null;
        this.#bundleData.selectedVariants.product2 = product2Data.variants?.[0] ?? null;
      }

      // Final safety: ensure selected variants are set
      if (!this.#bundleData.selectedVariants.product1) {
        this.#bundleData.selectedVariants.product1 = product1Data.variants?.[0] ?? null;
      }
      if (!this.#bundleData.selectedVariants.product2) {
        this.#bundleData.selectedVariants.product2 = product2Data.variants?.[0] ?? null;
      }

      this.#updateBundleDisplay();
    } catch (error) {
      console.error('Error initializing bundle:', error);
    }
  }

  /**
   * Load product data from Shopify API
   * @param {string} productId - The product ID
   * @returns {Promise<Object>} Product data
   */
  async #loadProductData(productId) {
    try {
      const response = await fetch(`/products/${productId}.js`);
      
      if (!response.ok) {
        throw new Error(`Failed to load product ${productId} - HTTP ${response.status}`);
      }
      
      const productData = await response.json();
      return productData;
    } catch (error) {
      console.error(`Error loading product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Bind event listeners
   */
  #bindEvents() {
    // Size picker change
    if (this.refs.bundleSizePicker) {
      this.refs.bundleSizePicker.addEventListener('change', this.#handleSizeChange.bind(this));
    }

    // Variant selectors change
    const variantSelectors = this.querySelectorAll('[data-bundle-variant-select]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.#handleVariantChange.bind(this));
    });

    // Quantity controls
    if (this.refs.bundleQuantity) {
      const minusBtn = this.querySelector('[data-quantity-minus]');
      const plusBtn = this.querySelector('[data-quantity-plus]');
      
      if (minusBtn) {
        minusBtn.addEventListener('click', this.#handleQuantityChange.bind(this, -1));
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', this.#handleQuantityChange.bind(this, 1));
      }
    }

    // Add to cart button
    if (this.refs.bundleAddToCart) {
      this.refs.bundleAddToCart.addEventListener('click', this.#handleAddToCart.bind(this));
    }
  }

  /**
   * Handle size selection change
   * @param {Event} event - Change event
   */
  #handleSizeChange(event) {
    const newSize = event.target.value;
    this.#bundleData.currentSize = newSize;

    // Update variants for both products based on size
    this.#updateVariantsForSize(newSize);
    this.#updateBundleDisplay();
  }

  /**
   * Handle variant selection change
   * @param {Event} event - Change event
   */
  #handleVariantChange(event) {
    const productNumber = event.target.dataset.product;
    const optionName = event.target.dataset.option;
    const selectedValue = event.target.value;

    // Validate that we have the required data
    if (!productNumber || !optionName || !selectedValue) {
      console.warn('Product Bundle: Missing required data for variant change');
      return;
    }

    // Find the variant that matches all current selections
    const product = productNumber === '1' ? this.#bundleData.product1 : this.#bundleData.product2;
    
    // Validate that product exists
    if (!product) {
      console.warn(`Product Bundle: Product ${productNumber} not found`);
      return;
    }

    const newVariant = this.#findVariantBySelections(product, optionName, selectedValue);

    if (newVariant) {
      this.#bundleData.selectedVariants[`product${productNumber}`] = newVariant;
      this.#updateBundleDisplay();
    }
  }

  /**
   * Find variant based on current selections
   * @param {Object} product - Product data
   * @param {string} changedOption - The option that was changed
   * @param {string} changedValue - The new value for the changed option
   * @returns {Object|null} Matching variant
   */
  #findVariantBySelections(product, changedOption, changedValue) {
    // Validate that product has required properties
    if (!product || !product.variants || !product.options) {
      console.warn('Product Bundle: Invalid product data for variant selection');
      return null;
    }

    const currentSelections = this.#getCurrentSelections(product);
    currentSelections[changedOption] = changedValue;

    return product.variants.find(variant => {
      return product.options.every((option, index) => {
        const optionName = option.name.toLowerCase().replace(/\s+/g, '-');
        const variantValue = variant[`option${index + 1}`].toLowerCase().replace(/\s+/g, '-');
        return currentSelections[optionName] === variantValue;
      });
    });
  }

  /**
   * Get current selections for a product
   * @param {Object} product - Product data
   * @returns {Object} Current selections
   */
  #getCurrentSelections(product) {
    const selections = {};
    
    // Validate that product and bundle data exist
    if (!product || !product.id || !this.#bundleData.product1 || !this.#bundleData.product2) {
      console.warn('Product Bundle: Missing product data for selections');
      return selections;
    }

    // Determine product number (1 or 2)
    let productNumber = '1';
    if (this.#bundleData.product2 && product.id === this.#bundleData.product2.id) {
      productNumber = '2';
    }

    const variantSelectors = this.querySelectorAll(`[data-product="${productNumber}"][data-bundle-variant-select]`);
    
    variantSelectors.forEach(selector => {
      const optionName = selector.dataset.option;
      selections[optionName] = selector.value;
    });

    // Add size if available
    if (this.#bundleData.currentSize) {
      selections['size'] = this.#bundleData.currentSize;
    }

    return selections;
  }

  /**
   * Update variants for both products based on selected size
   * @param {string} size - Selected size
   */
  #updateVariantsForSize(size) {
    // Update product 1 variants
    if (this.#bundleData.product1) {
      const newVariant1 = this.#findVariantBySizeAndCurrentSelections(this.#bundleData.product1, size, 1);
      if (newVariant1) {
        this.#bundleData.selectedVariants.product1 = newVariant1;
      }
    }

    // Update product 2 variants
    if (this.#bundleData.product2) {
      const newVariant2 = this.#findVariantBySizeAndCurrentSelections(this.#bundleData.product2, size, 2);
      if (newVariant2) {
        this.#bundleData.selectedVariants.product2 = newVariant2;
      }
    }
  }

  /**
   * Find variant by size
   * @param {Object} product - Product data
   * @param {string} size - Size to find
   * @returns {Object|null} Matching variant
   */
  #findVariantBySize(product, size) {
    const sizeOptionIndex = product.options.findIndex(option => 
      option.name.toLowerCase() === 'size'
    );

    if (sizeOptionIndex === -1) return null;

    // Find variant that matches the selected size
    const matchingVariant = product.variants.find(variant => {
      const variantSize = variant[`option${sizeOptionIndex + 1}`].toLowerCase().replace(/\s+/g, '-');
      return variantSize === size;
    });

    return matchingVariant;
  }

  /**
   * Find variant by size while preserving current selections for other options
   * @param {Object} product - Product data
   * @param {string} size - Size to find
   * @param {number} productNumber - Product number (1 or 2)
   * @returns {Object|null} Matching variant
   */
  #findVariantBySizeAndCurrentSelections(product, size, productNumber) {
    const sizeOptionIndex = product.options.findIndex(option => 
      option.name.toLowerCase() === 'size'
    );

    if (sizeOptionIndex === -1) return null;

    // Get current selections for this product (excluding size)
    const currentSelections = this.#getCurrentSelections(product);
    
    // Find variant that matches both the new size AND current selections
    const matchingVariant = product.variants.find(variant => {
      // Check if size matches
      const variantSize = variant[`option${sizeOptionIndex + 1}`].toLowerCase().replace(/\s+/g, '-');
      if (variantSize !== size) return false;

      // Check if all other current selections match
      return product.options.every((option, index) => {
        const optionName = option.name.toLowerCase().replace(/\s+/g, '-');
        const variantValue = variant[`option${index + 1}`].toLowerCase().replace(/\s+/g, '-');
        
        // Skip size option since we already checked it
        if (index === sizeOptionIndex) return true;
        
        // Check if this option matches current selection
        return currentSelections[optionName] === variantValue;
      });
    });

    // If no exact match found, fall back to any variant with the correct size
    if (!matchingVariant) {
      return this.#findVariantBySize(product, size);
    }

    return matchingVariant;
  }

  /**
   * Handle quantity change
   * @param {number} change - Change amount (-1 or 1)
   */
  #handleQuantityChange(change) {
    const currentQuantity = parseInt(this.refs.bundleQuantity.value) || 1;
    const newQuantity = Math.max(1, currentQuantity + change);
    
    this.refs.bundleQuantity.value = newQuantity;
    if (this.refs.bundleFormQuantity) {
      this.refs.bundleFormQuantity.value = newQuantity;
    }
  }

  /**
   * Handle add to cart
   * @param {Event} event - Click event
   */
  async #handleAddToCart(event) {
    event.preventDefault();

    if (this.#isLoading) return;

    const button = event.target.closest('button') ?? event.target;
    const contentSpan = button.querySelector('.bundle-add-to-cart-text__content');
    const originalLabel = contentSpan?.getAttribute('data-original-text') || contentSpan?.textContent?.trim() || 'Add Bundle to Cart';

    try {
      this.#isLoading = true;
      button.disabled = true;
      if (contentSpan) {
        contentSpan.textContent = 'Adding...';
      }

      // Generate unique bundle ID for this specific bundle
      const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Get the main product (the one that contains the bundle block)
      const mainProductId = this.getAttribute('data-product-1-id') || this.getAttribute('data-product-1-handle') || '';
      const mainProductHandle = this.getAttribute('data-main-product-handle') || '';
      const mainProduct = this.#bundleData.product1;

      // Ensure variants are selected before constructing payload
      const selectedVariant1 = this.#bundleData.selectedVariants.product1 || this.#bundleData.product1?.variants?.[0] || null;
      const selectedVariant2 = this.#bundleData.selectedVariants.product2 || this.#bundleData.product2?.variants?.[0] || null;

      if (!selectedVariant1 || !selectedVariant1.id || !selectedVariant2 || !selectedVariant2.id) {
        throw new Error('Variant selection is incomplete. Please choose options before adding to cart.');
      }
      
      // Prepare bundle information for cart properties
      const bundleInfo = {
        '_bundle_product': 'true',
        '_bundle_id': bundleId,
        '_bundle_discount': this.#bundleData.discountPercentage,
        '_bundle_main_product_handle': mainProductHandle,
        '_bundle_product_1_id': selectedVariant1.id,
        '_bundle_product_2_id': selectedVariant2.id,
        '_bundle_product_1_title': this.#bundleData.product1.title,
        '_bundle_product_2_title': this.#bundleData.product2.title,
        '_bundle_size': this.#bundleData.currentSize || '',
        '_bundle_variants': JSON.stringify({
          product1: {
            id: selectedVariant1.id,
            title: this.#bundleData.product1.title,
            options: this.#getVariantOptions(selectedVariant1, this.#bundleData.product1)
          },
          product2: {
            id: selectedVariant2.id,
            title: this.#bundleData.product2.title,
            options: this.#getVariantOptions(selectedVariant2, this.#bundleData.product2)
          }
        })
      };

      // Add both bundle products to cart with bundle properties
      const items = [
        {
          id: selectedVariant1.id,
          quantity: parseInt(this.refs.bundleQuantity.value) || 1,
          properties: bundleInfo
        },
        {
          id: selectedVariant2.id,
          quantity: parseInt(this.refs.bundleQuantity.value) || 1,
          properties: bundleInfo
        }
      ];

      // Add items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Failed to add bundle to cart');
      }

      const cartData = await response.json();

      // Animate "Added" state on the button and swap label
      this.#animateAddedState(button, originalLabel);

      // Trigger bundle added event for discount API
      this.dispatchEvent(new CustomEvent('bundle-added-to-cart', {
        detail: { 
          bundleProducts: [
            this.#bundleData.product1,
            this.#bundleData.product2
          ],
          bundleId: bundleId,
          cart: cartData
        },
        bubbles: true
      }));

      // Fetch full cart to get accurate item_count, then dispatch cart update
      try {
        const cartResponse = await fetch('/cart.js');
        const fullCart = await cartResponse.json();
        this.dispatchEvent(new CartUpdateEvent(fullCart, 'product-bundle-component', {
          itemCount: fullCart.item_count,
          source: 'product-bundle',
        }));
      } catch (_) {
        // Fallback: dispatch minimal event if cart fetch fails
        this.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
          detail: { cart: cartData, data: { source: 'product-bundle' } },
          bubbles: true
        }));
      }

      // Show success message
      this.#showSuccessMessage();

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      
      // Trigger cart error event
      this.dispatchEvent(new CustomEvent(ThemeEvents.cartError, {
        detail: { error: error.message },
        bubbles: true
      }));

      this.#showErrorMessage(error.message);
    } finally {
      this.#isLoading = false;
      button.disabled = false;
      // If the added animation is active, let it restore the label; otherwise restore now
      if (!button.classList.contains('bundle-atc-added') && contentSpan) {
        contentSpan.textContent = originalLabel;
      }
    }
  }

  /**
   * Briefly shows the Added state by toggling a CSS class on the button
   * @param {HTMLElement} button
   */
  #animateAddedState(button, originalLabel) {
    try {
      const contentSpan = button.querySelector('.bundle-add-to-cart-text__content');
      if (contentSpan) {
        contentSpan.textContent = 'Added';
      }
      if (!button.classList.contains('bundle-atc-added')) {
        button.classList.add('bundle-atc-added');
      }
      setTimeout(() => {
        button.classList.remove('bundle-atc-added');
        if (contentSpan) {
          contentSpan.textContent = originalLabel || 'Add Bundle to Cart';
        }
      }, 2000);
    } catch (_) {
      // no-op
    }
  }

  /**
   * Get variant options as a formatted string
   * @param {Object} variant - The variant object
   * @param {Object} product - The product object
   * @returns {Object} Formatted options object
   */
  #getVariantOptions(variant, product) {
    const options = {};
    product.options.forEach((option, index) => {
      options[option.name] = variant[`option${index + 1}`];
    });
    return options;
  }

  /**
   * Update bundle display with current selections
   */
  #updateBundleDisplay() {
    this.#updateProductDisplay(1);
    this.#updateProductDisplay(2);
    this.#updateTotalPrice();
    this.#updateMainProductPrice();
  }

  /**
   * Update display for a specific product
   * @param {number} productNumber - Product number (1 or 2)
   */
  #updateProductDisplay(productNumber) {
    const product = productNumber === 1 ? this.#bundleData.product1 : this.#bundleData.product2;
    const variant = this.#bundleData.selectedVariants[`product${productNumber}`];

    if (!product || !variant) return;

    // Update subtitle
    const subtitleElement = this.querySelector(`[data-product-${productNumber}-subtitle]`);
    if (subtitleElement) {
      const subtitleParts = [];
      
      // Add size if available
      if (this.#bundleData.currentSize) {
        subtitleParts.push(this.#bundleData.currentSize);
      }

      // Add other options
      product.options.forEach((option, index) => {
        if (option.name.toLowerCase() !== 'size') {
          const value = variant[`option${index + 1}`];
          subtitleParts.push(value);
        }
      });

      subtitleElement.textContent = subtitleParts.join(' / ');
    }

    // Update price using existing price structure
    const priceElement = this.querySelector(`[data-product-${productNumber}-price]`);
    if (priceElement) {
      const originalPrice = variant.price;
      const discountedPrice = originalPrice * (1 - this.#bundleData.discountPercentage / 100);

      // Update original price span
      const originalPriceSpan = priceElement.querySelector('.original-price');
      if (originalPriceSpan) {
        originalPriceSpan.textContent = this.#formatMoney(originalPrice);
      }

      // Update discounted price span
      const discountedPriceSpan = priceElement.querySelector('.discounted-price');
      if (discountedPriceSpan) {
        discountedPriceSpan.textContent = this.#formatMoney(discountedPrice);
      }
    }
  }

  /**
   * Update total bundle price
   */
  #updateTotalPrice() {
    const variant1 = this.#bundleData.selectedVariants.product1;
    const variant2 = this.#bundleData.selectedVariants.product2;

    if (!variant1 || !variant2) return;

    const totalOriginalPrice = variant1.price + variant2.price;
    const totalDiscountedPrice = totalOriginalPrice * (1 - this.#bundleData.discountPercentage / 100);

    const totalPriceElement = this.querySelector('[data-bundle-total-price]');
    if (totalPriceElement) {
      // Update total original price span
      const totalOriginalPriceSpan = totalPriceElement.querySelector('.total-original-price');
      if (totalOriginalPriceSpan) {
        totalOriginalPriceSpan.textContent = this.#formatMoney(totalOriginalPrice);
      }

      // Update total discounted price span
      const totalDiscountedPriceSpan = totalPriceElement.querySelector('.total-discounted-price');
      if (totalDiscountedPriceSpan) {
        totalDiscountedPriceSpan.textContent = this.#formatMoney(totalDiscountedPrice);
      }
    }
  }

  /**
   * Hide main product prices initially
   */
  #hideMainProductPrices() {
    const mainProductPrices = document.querySelectorAll('product-price[data-product-id]');
    mainProductPrices.forEach(priceElement => {
      const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
      if (priceContainer) {
        priceContainer.style.display = 'none';
      }
    });
  }

  /**
   * Update main product price to show bundle discount
   */
  #updateMainProductPrice() {
    const variant1 = this.#bundleData.selectedVariants.product1;
    const variant2 = this.#bundleData.selectedVariants.product2;

    if (!variant1 || !variant2) return;

    const totalOriginalPrice = variant1.price + variant2.price;
    const totalDiscountedPrice = totalOriginalPrice * (1 - this.#bundleData.discountPercentage / 100);

    // Find all product-price elements on the page (main product price sections)
    const mainProductPrices = document.querySelectorAll('product-price[data-product-id]');
    
    mainProductPrices.forEach(priceElement => {
      const priceContainer = priceElement.querySelector('[ref="priceContainer"]');
      if (priceContainer) {
        // Show the price container if it was hidden
        priceContainer.style.display = 'flex';
        
        // Clear existing content and create proper structure
        priceContainer.innerHTML = '';
        
        // Create the compare-at-price structure (original price)
        const compareAtPriceGroup = document.createElement('span');
        compareAtPriceGroup.setAttribute('role', 'group');
        
        const compareAtPriceLabel = document.createElement('span');
        compareAtPriceLabel.className = 'visually-hidden';
        compareAtPriceLabel.textContent = 'Regular price ';
        
        const compareAtPriceSpan = document.createElement('span');
        compareAtPriceSpan.className = 'compare-at-price';
        compareAtPriceSpan.textContent = this.#formatMoney(totalOriginalPrice);
        
        compareAtPriceGroup.appendChild(compareAtPriceLabel);
        compareAtPriceGroup.appendChild(compareAtPriceSpan);
        
        // Create the main price structure (discounted price)
        const priceGroup = document.createElement('span');
        priceGroup.setAttribute('role', 'group');
        
        const priceLabel = document.createElement('span');
        priceLabel.className = 'visually-hidden';
        priceLabel.textContent = 'Sale price ';
        
        const priceSpan = document.createElement('span');
        priceSpan.className = 'price';
        priceSpan.textContent = this.#formatMoney(totalDiscountedPrice);
        
        priceGroup.appendChild(priceLabel);
        priceGroup.appendChild(priceSpan);
        
        // Add both groups to the container
        priceContainer.appendChild(compareAtPriceGroup);
        priceContainer.appendChild(priceGroup);
      }
    });
  }

  /**
   * Format money value
   * @param {number} amount - Amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  }

  /**
   * Show success message
   */
  #showSuccessMessage() {
    // You can implement a toast notification or other success feedback here
    console.log('Bundle added to cart successfully!');
  }

  /**
   * Show error message
   * @param {string} message - Error message
   */
  #showErrorMessage(message) {
    // You can implement error notification here
    console.error('Error:', message);
  }
}

// Register the component
customElements.define('product-bundle-component', ProductBundleComponent);
